import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EVENT } from "@/data/site";
import { CheckCircle2, Calendar, MapPin, Loader2, Mic, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const REGISTRATION_START = new Date("2027-02-22T00:00:00");

export default function Register() {
  useDocumentTitle("Zapisz się", "Zapisz się na mamy!kongres 2027. Wypełnij formularz — resztą zajmiemy się my.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const started = now >= REGISTRATION_START;
  const diff = Math.max(0, REGISTRATION_START - now);
  const countdown = [
    { v: Math.floor(diff / 86400000), l: "dni" },
    { v: Math.floor(diff / 3600000) % 24, l: "godzin" },
    { v: Math.floor(diff / 60000) % 60, l: "minut" },
    { v: Math.floor(diff / 1000) % 60, l: "sekund" },
  ];

  const update = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: null });
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Podaj imię i nazwisko.";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Podaj poprawny e-mail.";
    if (form.phone.trim().length < 6) e.phone = "Podaj numer telefonu.";
    if (!form.consent) e.consent = "Zgoda jest wymagana.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
      toast.success("Dziękujemy! Wysłałyśmy Ci maila z potwierdzeniem.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Coś poszło nie tak. Spróbuj ponownie.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="register-success">
        <CheckCircle2 className="w-16 h-16 text-[#E6007A] mx-auto mb-6" />
        <h1 className="font-display text-4xl md:text-5xl mb-4">Jesteś na liście!</h1>
        <p className="text-lg text-[#5C534D] mb-8">
          Wysłałyśmy potwierdzenie na <span className="font-semibold text-[#2B2623]">{form.email}</span>.
          W ciągu 24 godzin dostaniesz dane do płatności.
        </p>
        <div className="rounded-2xl bg-[#F4EFE6] p-6 inline-block text-left">
          <div className="flex items-center gap-3 mb-2"><Calendar className="w-5 h-5 text-[#E6007A]" /><span>{EVENT.dateLong}</span></div>
          <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[#E6007A]" /><span>{EVENT.city} · {EVENT.venue}</span></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 md:py-16 grid lg:grid-cols-5 gap-14">
      <div className="lg:col-span-2">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4">Zapisz się</div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[1.02] mb-6">
          Start zapisów:<br /><span className="italic text-[#E6007A]">22 lutego 2027</span>.
        </h1>
        <p className="text-[#5C534D] text-lg leading-relaxed mb-8">
          Rezerwuj swoje miejsce już pierwszego dnia.
        </p>

        <div className="rounded-3xl bg-[#F4EFE6] p-6 space-y-3">
          <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[#E6007A]" /><span className="font-medium">{EVENT.dateLong}</span></div>
          <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[#E6007A]" /><span className="font-medium">{EVENT.city} · {EVENT.venue}</span></div>
          <div className="pt-3 border-t border-[#E8E1D9]/60 flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-[#E6007A]">Early Bird od 22 lutego 2027 do 22 marca 2027</span>
            <span className="font-display text-2xl text-[#E6007A] whitespace-nowrap shrink-0">250 zł</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-[#E6007A]">Bilety standard od 23 marca 2027</span>
            <span className="font-display text-2xl text-[#E6007A] whitespace-nowrap shrink-0">299 zł</span>
          </div>
        </div>
      </div>

      {started ? (
      <form onSubmit={submit} className="lg:col-span-3 rounded-3xl bg-white border border-[#E8E1D9] p-8 md:p-10" data-testid="register-form">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="name">Imię i nazwisko</label>
            <input
              id="name"
              data-testid="reg-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Anna Kowalska"
              className={`w-full rounded-xl border px-4 py-3 bg-white focus:border-[#E6007A] outline-none ${errors.name ? "border-red-400" : "border-[#E8E1D9]"}`}
            />
            {errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="email">Email</label>
            <input
              id="email"
              data-testid="reg-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="anna@example.pl"
              className={`w-full rounded-xl border px-4 py-3 bg-white focus:border-[#E6007A] outline-none ${errors.email ? "border-red-400" : "border-[#E8E1D9]"}`}
            />
            {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="phone">Telefon</label>
            <input
              id="phone"
              data-testid="reg-phone"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+48 500 123 456"
              className={`w-full rounded-xl border px-4 py-3 bg-white focus:border-[#E6007A] outline-none ${errors.phone ? "border-red-400" : "border-[#E8E1D9]"}`}
            />
            {errors.phone && <div className="text-red-500 text-sm mt-1">{errors.phone}</div>}
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="reg-consent"
              checked={form.consent}
              onChange={update("consent")}
              className="mt-1 w-5 h-5 accent-[#E6007A]"
            />
            <span className="text-sm text-[#5C534D] leading-relaxed">
              Wyrażam zgodę na przetwarzanie moich danych w celu obsługi zapisu na kongres.
              Więcej w{" "}
              <Link to="/polityka-prywatnosci" className="text-[#E6007A] font-semibold link-underline">
                polityce prywatności
              </Link>.
            </span>
          </label>
          {errors.consent && <div className="text-red-500 text-sm">{errors.consent}</div>}

          <button
            type="submit"
            data-testid="reg-submit"
            disabled={submitting}
            className="btn-lift w-full rounded-full bg-[#E6007A] text-white font-semibold py-4 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Wysyłamy..." : "Zapisuję się"}
          </button>
        </div>
      </form>
      ) : (
      <div
        data-testid="registration-countdown"
        className="lg:col-span-3 rounded-3xl bg-[#F4EFE6] border border-[#E8E1D9] p-10 md:p-14 flex flex-col items-center justify-center text-center min-h-[420px]"
      >
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-8">Otwieramy zapisy za</div>
        <div className="flex items-start gap-5 md:gap-10">
          {countdown.map((u) => (
            <div key={u.l} data-testid={`countdown-${u.l}`}>
              <div className="font-display text-5xl md:text-7xl text-[#E6007A] leading-none tabular-nums">
                {String(u.v).padStart(2, "0")}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#5C534D] mt-3">{u.l}</div>
            </div>
          ))}
        </div>
        <p className="text-[#5C534D] leading-relaxed mt-10 max-w-md">
          Zapisz datę w kalendarzu: 22 lutego 2027 ruszamy z zapisami.
          Najpierw pula Early Bird — warto być pierwszą.
        </p>
      </div>
      )}
    </div>

    {/* ZAPISY NA WARSZTATY */}
    <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24" data-testid="workshops-section">
      <div className="rounded-3xl bg-white border border-[#E8E1D9] p-8 md:p-12">
        <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-3">Zapisy na warsztaty</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight mb-3">
              Wybierz swoje <span className="italic text-[#E6007A]">warsztaty</span>.
            </h2>
            <p className="text-[#5C534D] max-w-2xl leading-relaxed">
              W ciągu dnia odbędą się cztery bloki warsztatów. W każdym bloku do wyboru 10 równoległych
              warsztatów po 20 osób. Zapisy otwieramy po zakupie biletu — potwierdzenie wyślemy e-mailem.
            </p>
          </div>
          <div className="rounded-full bg-[#F4EFE6] text-[#E6007A] px-4 py-2 text-sm font-semibold">
            Już wkrótce
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Blok 1", time: "11:45 – 12:30" },
            { label: "Blok 2", time: "12:45 – 13:30" },
            { label: "Blok 3", time: "15:40 – 16:25" },
            { label: "Blok 4", time: "16:30 – 17:15" },
          ].map((b, i) => (
            <div
              key={i}
              className="rounded-2xl bg-[#F4EFE6] p-5"
              data-testid={`workshop-slot-${i + 1}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Mic className={`w-4 h-4 ${i % 2 ? "text-[#2E4331]" : "text-[#E6007A]"}`} />
                <span className="font-semibold text-[#2B2623]">{b.label}</span>
              </div>
              <div className="flex items-center gap-2 text-[#5C534D] text-sm">
                <Clock className="w-4 h-4 text-[#E6007A]" />
                <span>{b.time}</span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-[#5C534D] mt-8">
          Zapisy na konkretne warsztaty uruchomimy bliżej wydarzenia. Osoby z biletem Early Bird dostaną
          dostęp do zapisów jako pierwsze.
        </p>
      </div>
    </section>
    </>
  );
}
